import { useState } from 'react';
import { TableRow, TableCell, IconButton } from '@material-ui/core';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { getEditValues } from '../form';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import ReadMoreLess from '~/ui/components/common/ReadMoreLess';

import { Timezone } from '~/types';
import { IFormValues } from '../../popups/MedicalNoteModal/form/types';
import { IPagePermissions } from '../../../types';
import styles from './Item.module.scss';
import { ICarePlanMedicalNote } from '~/services/api/carePlanMedicalNotes/types';
import MedicalNoteModal from '../../popups/MedicalNoteModal/MedicalNoteModal';

interface IProps {
  item: ICarePlanMedicalNote;
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Item = ({ item, permissions, onCarePlanChange }: IProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const { onUpdateCarePlanMedicalNote, onRemoveCarePlanMedicalNote } = useStoreActions(
    actions => actions.carePlanMedicalNotes,
  );
  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleUpdateMedicalNote = async (values: IFormValues) => {
    const payload = {
      ...values,
      id: item.id,
      date: isoToFormat(values.date, 'y-MM-dd'),
    };

    try {
      setIsUpdating(true);
      await onUpdateCarePlanMedicalNote(payload);
      onCarePlanChange();
      setShowEdit(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveMedicalNote = async () => {
    try {
      setIsRemoving(true);
      await onRemoveCarePlanMedicalNote(item.id);
      onCarePlanChange();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsRemoving(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>{isoToFormat(item.date, 'dd MMM y', Timezone.GMT, false)}</TableCell>
        <TableCell>
          <ReadMoreLess content={item.reason} />
        </TableCell>
        {permissions.canManage && (
          <TableCell align="right">
            <IconButton
              className={styles.iconButton}
              title="Edit"
              onClick={() => setShowEdit(true)}
            >
              <CreateIcon className={styles.icon} />
            </IconButton>
            <IconButton
              className={styles.iconButton}
              title="Remove"
              onClick={() => setShowRemove(true)}
            >
              <DeleteIcon className={styles.icon} />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {showEdit && (
        <MedicalNoteModal
          mode="edit"
          initialValues={getEditValues(item)}
          isProcessing={isUpdating}
          onSubmit={handleUpdateMedicalNote}
          onClose={() => setShowEdit(false)}
        />
      )}
      {showRemove && (
        <ConfirmModal
          isLoading={isRemoving}
          title="Remove Medical Note"
          description="Are you sure you want to remove current Medical Note?"
          onClose={() => setShowRemove(false)}
          onConfirm={handleRemoveMedicalNote}
        />
      )}
    </>
  );
};

export default Item;
